import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import {
  Dialog,
  Typography,
  useMediaQuery,
  makeStyles,
  createStyles,
  useTheme,
  Container,
} from '@material-ui/core'
import Button from '../Button/Button'
import dayjs from 'dayjs'
import CloseIcon from '@material-ui/icons/Close'
import { useRouter } from 'next/router'
import DatePicker, { registerLocale } from 'react-datepicker'

//Add locale translations of date picker
import fr from 'date-fns/locale/fr'
import de from 'date-fns/locale/de'
import es from 'date-fns/locale/es'
import nl from 'date-fns/locale/nl'

import { Cart, UnAvailability } from '../../typings/base'
import CMSText from '../Global/CMSText'
import { RMSDateFormat, SyliusDateFormat } from '../../utils/constants'

registerLocale('fr', fr)
registerLocale('de', de)
registerLocale('es', es)
registerLocale('nl', nl)

const useStyles = makeStyles((theme) =>
  createStyles({
    modal: {
      '& .MuiDialog-paper': {
        borderRadius: 0,
        padding: 0,
        background: 'none',
        boxShadow: 'none',
      },
      '& .react-datepicker': {
        justifyContent: 'center',
        minHeight: 325, //avoid calendar jump if month have a week more than others
        '&__navigation': {
          borderRadius: 0,
          '&--next': {
            '&--disabled': {
              opacity: 0,
            },
          },
        },
        '&__day': {
          pointerEvents: 'none',
          userSelect: 'none',
          verticalAlign: 'top',
          '&--outside-month': {
            //display: 'none',
          },
          '&--excluded': {
            color: theme.extraColors.greyText,
            textDecoration: 'line-through',
          },
        },
      },
    },
    text: {
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      '&:before': {
        content: '" "',
        display: 'inline-block',
        width: '0.5em',
        height: '0.5em',
        borderRadius: '50%',
        marginRight: '1ch',
        verticalAlign: 'middle',
        background: theme.palette.error.main,
      },
    },
    link: {
      cursor: 'pointer',
      padding: 0,
      textDecoration: 'underline',
      color: theme.palette.primary.main,
      '&:hover, &:focus': {
        textDecoration: 'none',
      },
    },
    datePicker: {},
    content: {
      padding: theme.spacing(0, 0, 1),
      backgroundColor: theme.palette.common.white,
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[1],
    },
    head: {
      padding: theme.spacing(2),
    },
    headTitle: {},
    headDesc: {
      '& p': {
        margin: 0,
      },
    },
    close: {
      display: 'flex',
      justifyContent: 'flex-end',
      color: theme.palette.common.white,
      padding: theme.spacing(1, 0),
    },
    closeBtn: {
      padding: 0,
      color: theme.palette.common.white,
      minWidth: theme.spacing(4),
      '&:hover, &:focus': {
        background: 'none',
      },
    },
  })
)

type TProps = {
  endLeasingDate: number
  unvailabilities: UnAvailability[]
  t: any
  texts: any
  cart: Cart
}

const AvailabilityChecker = ({
  unvailabilities,
  endLeasingDate,
  t,
  texts,
  cart,
}: TProps) => {
  const customCSSRoot = 'availability-checker'
  const classes = useStyles()
  const theme = useTheme()
  const [open, setOpen] = useState(false)
  const [excludedDates, setExcludedDates] = useState([])
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const router = useRouter()
  const currentLocale = router?.locale ? router.locale : 'fr'
  const startDate =
    cart &&
    cart.item &&
    cart.item.startLeasingDate !== undefined &&
    dayjs(cart.item.startLeasingDate, 'DD/MM/YYYY').toDate()
  const maxEndDate = dayjs(cart?.item?.startLeasingDate, SyliusDateFormat).add(
    endLeasingDate - 1,
    'day'
  )

  const handleClose = () => {
    setOpen(false)
  }
  const handleAvailabilityCheck = () => {
    setOpen(true)
  }

  useEffect(() => {
    const excludedDatesArr = []
    // First check if they are unvailabilities
    if (unvailabilities.length > 0) {
      // if yes, iterate on each object and add it to the excluded dates array
      unvailabilities.forEach((unvailability) => {
        const startDate = dayjs(unvailability.startDate, RMSDateFormat)
        // if end date is not defined (with empty string), we use end date defined in redux parameter.
        // it will show unavailibity until the end of leasing
        const endDate =
          unvailability.endDate === ''
            ? dayjs(maxEndDate, SyliusDateFormat)
            : dayjs(unvailability.endDate, RMSDateFormat)

        for (let date = startDate; date <= endDate; date = date.add(1, 'day')) {
          excludedDatesArr.push(date.toDate())
        }
        setExcludedDates(excludedDatesArr)
      })
    }
  }, [unvailabilities])

  return (
    <>
      <Typography
        variant="caption"
        component={'div'}
        className={clsx(`${customCSSRoot}__text`, classes.text)}
      >
        <CMSText
          asText
          defaultText={t('texts:single_product:availability_checker:text')}
          data={texts?.availability_checker_text}
        />
        &nbsp;
        <span
          role="button"
          className={clsx(`${customCSSRoot}__link`, classes.link)}
          onClick={() => handleAvailabilityCheck()}
        >
          <CMSText
            asText
            defaultText={t('texts:single_product:availability_checker:link')}
            data={texts?.availability_checker_link}
          />
        </span>
      </Typography>
      <Dialog
        open={open}
        maxWidth={'lg'}
        className={clsx(`${customCSSRoot}__modal`, classes.modal)}
        scroll={'paper'}
        disableBackdropClick
        onClose={handleClose}
      >
        <div className={clsx(`${customCSSRoot}__modal-close`, classes.close)}>
          <Button
            disableRipple
            onClick={handleClose}
            className={clsx(
              `${customCSSRoot}__modal-close__btn`,
              classes.closeBtn
            )}
          >
            <CloseIcon />
            <Typography variant="srOnly">
              {t('texts:single_product:availability_checker:modal_close')}
            </Typography>
          </Button>
        </div>
        <div
          className={clsx(`${customCSSRoot}__modal-content`, classes.content)}
        >
          <Container
            className={clsx(`${customCSSRoot}__modal-head`, classes.head)}
          >
            <Typography
              variant="h4"
              component={'h2'}
              className={clsx(
                `${customCSSRoot}__modal-head__title`,
                classes.headTitle
              )}
            >
              <CMSText
                asText
                defaultText={t(
                  'texts:single_product:availability_checker:modal_title'
                )}
                data={texts?.availability_checker_modal_title}
              />
            </Typography>
            <Typography
              variant="caption"
              component={'div'}
              className={clsx(
                `${customCSSRoot}__modal-head__desc`,
                classes.headDesc
              )}
            >
              <CMSText
                defaultText={t(
                  'texts:single_product:availability_checker:modal_description'
                )}
                data={texts?.availability_checker_modal_description}
              />
            </Typography>
          </Container>
          <DatePicker
            locale={currentLocale}
            onChange={() => startDate}
            openToDate={startDate}
            minDate={startDate}
            maxDate={dayjs(maxEndDate, SyliusDateFormat).toDate()}
            monthsShown={isMobile ? 1 : 2}
            inline
            excludeDates={excludedDates}
            className={clsx(`${customCSSRoot}__datepicker`, classes.datePicker)}
            showDisabledMonthNavigation
          />
        </div>
      </Dialog>
    </>
  )
}

export default AvailabilityChecker
